import { AfterViewInit, Component, ElementRef, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { DialogRef } from '../../services/modal.service';
import { forkJoin } from 'rxjs';
import { MapComponent } from '../../components/map/map.component';
import {
  CerealOutcome,
  Country,
  FallowAge,
  FallowType,
  PreviousCrop,
  Soil,
  SoilDepth,
  Specy,
  Variety,
} from '../../models/lists.model';
import { Parcel } from '../../models/parcel.model';
import { CampaignYearPipe } from '../../pipes/campaignYear.pipe';
import { CapitalizePipe } from '../../pipes/capitalize.pipe';
import { HectareConverterPipe } from '../../pipes/hectareConverter.pipe';
import { VarietyLabelPipe } from '../../pipes/varietyLabel.pipe';
import { CampaignService } from '../../services/campaign.service';
import { ListsService, ListType } from '../../services/lists.service';
import { MapService } from '../../services/map.service';
import { ParcelService } from '../../services/parcel.service';
import { TranslatePipe, TranslateDatePipe, TranslateWithPunctuationPipe } from '../../pipes/translate.pipe';
import { CommonModule } from '@angular/common';

export class ParcelDetailsDialogContext {
  parcelId: number;
  campaignId: number;
  showImport: boolean;
  showEdit: boolean;
  showClose: boolean;
  isBlocking: boolean;
  size: string;
}

@Component({
  standalone: true,
  selector: 'new-parcel-dlg',
  styleUrls: ['parcel-details-dialog.component.scss'],
  templateUrl: 'parcel-details-dialog.component.html',
  providers: [ParcelService, MapService],
  imports: [MapComponent, CampaignYearPipe, VarietyLabelPipe, CapitalizePipe, HectareConverterPipe, TranslatePipe, TranslateDatePipe, TranslateWithPunctuationPipe, CommonModule],
})
export class ParcelDetailsDialogComponent implements OnInit, AfterViewInit {
  context: ParcelDetailsDialogContext;

  parcel: Parcel;
  varieties: Variety[];

  map: L.Map;
  drawnItems: L.FeatureGroup;

  constructor(
    public dialog: DialogRef<ParcelDetailsDialogContext>,
    private mapService: MapService,
    private parcelService: ParcelService,
    private listService: ListsService,
    public campaignService: CampaignService,
    public router: Router,
    private _elementRef: ElementRef,
  ) {
    this.drawnItems = L.featureGroup();
    this.context = dialog.context;

    const obs1 = this.parcelService.getParcel(this.context.parcelId);
    const obs2 = this.getLists();
    forkJoin([obs1, obs2]).subscribe((data: Array<any>) => {
      this.handleParcel(data[0]);
      // jointure parcelle avec les objets des listes (pour labels)
      if (this.parcel.country) {
        this.parcel.country = data[1]
          .get(ListType.COUNTRIES)
          .find((country: Country) => country.id === this.parcel.country.id);
      }
      if (this.parcel.specy) {
        this.parcel.specy = data[1].get(ListType.SPECIES).find((specy: Specy) => specy.id === this.parcel.specy.id);
      }
      if (this.parcel.soilDepth) {
        this.parcel.soilDepth = data[1]
          .get(ListType.SOIL_DEPTHS)
          .find((soilDepth: SoilDepth) => soilDepth.id === this.parcel.soilDepth.id);
      }
      if (this.parcel.previousCrop) {
        this.parcel.previousCrop = data[1]
          .get(ListType.PREVIOUS_CROPS)
          .find((previousCrop: PreviousCrop) => previousCrop.id === this.parcel.previousCrop.id);
      }
      if (this.parcel.fallowType) {
        this.parcel.fallowType = data[1]
          .get(ListType.FALLOW_TYPES)
          .find((fallowType: FallowType) => fallowType.id === this.parcel.fallowType.id);
      }
      if (this.parcel.fallowAge) {
        this.parcel.fallowAge = data[1]
          .get(ListType.FALLOW_AGES)
          .find((fallowAge: FallowAge) => fallowAge.id === this.parcel.fallowAge.id);
      }
      if (this.parcel.cerealOutcome) {
        this.parcel.cerealOutcome = data[1]
          .get(ListType.CEREAL_OUTCOMES)
          .find((cerealOutcome: CerealOutcome) => cerealOutcome.id === this.parcel.cerealOutcome.id);
      }

      // besoin de la liste des varieties pour setter le label avec varietySpecy quand variety.code = OTHER
      this.varieties = data[1].get(ListType.VARIETIES);
      if (this.parcel.variety) {
        this.parcel.variety = this.varieties.find((variety: Variety) => variety.id === this.parcel.variety.id);
      }

      // TODO combiner avec la séquence d'Observable ?
      this.getSoils().subscribe((soils) => {
        if (this.parcel.soil) {
          this.parcel.soil = soils.find((soil: Soil) => soil.id === this.parcel.soil.id);
        }
      });
    });
  }

  getLists() {
    const listTypes = [
      ListType.COUNTRIES,
      ListType.SPECIES,
      ListType.SOIL_DEPTHS,
      ListType.PREVIOUS_CROPS,
      ListType.VARIETIES,
      ListType.CEREAL_OUTCOMES,
      ListType.FALLOW_TYPES,
      ListType.FALLOW_AGES,
    ];
    return this.listService.getListTypes(ListType, ...listTypes);
  }

  getSoils() {
    const postalCode = this.parcel.postalCode;
    const country = this.parcel.country.code;
    return this.listService.getSoils(country, postalCode);
  }

  ngOnInit() {}

  ngAfterViewInit() {
    L.Icon.Default.imagePath = 'assets/img/vendor/leaflet';
  }

  handleParcel(parcel: Parcel) {
    this.parcel = parcel;
    if (parcel.geometry) {
      const poly = new L.Polygon(
        parcel.geometry.coordinates.map((c) => c.map((latLng) => [latLng[1], latLng[0]])) as any,
      );
      this.drawnItems.addLayer(poly);
      this.map.fitBounds(poly.getBounds());
    }
  }

  close() {
    this.dialog.close();
  }

  importParcel() {
    this.router.navigate(['/parcels', 'new', this.context.campaignId, this.context.parcelId]);
    this.dialog.close();
  }

  editParcel() {
    this.router.navigate(['/parcels', 'edit', this.parcel.id]);
    this.dialog.close();
  }

  beforeDismiss(): boolean {
    console.log('bd');
    return false;
  }

  beforeClose(): boolean {
    console.log('bc');
    return false;
  }
}
